import { useCallback, useEffect, useRef, useState } from "react";
import { timecodeSpec } from "@infoflyt/shared/timecode";
import type { Oekt } from "../state/session";
import * as nettlogg from "../state/nettlogg";
import * as avslutt from "../state/avsluttdagen";

/**
 * Avslutt dagen, fra nettflata.
 *
 * ========  DEN BOR I MENYEN  ========
 *
 * Brukeren 03.11.2026: «Jeg ønsker heller at avslutt dagen skal ligge i
 * menyen, slik den gjør i appen, istedenfor å ha den under liveloggen.»
 *
 * Den sto nederst på loggen i ett døgn, og det var feil sted av samme grunn
 * som i appen: loggen er flata man skriver på hele dagen, og det som
 * avslutter den skal ikke ligge i veien for den. Den brukes én gang per dag.
 *
 * ========  DEN HENTER SITT EGET  ========
 *
 * Anker, økt og poster slås opp her og ikke sendes inn: flata åpnes fra
 * menyen, og den som åpner den kan stå hvor som helst. Det er den SAMME
 * økta liveloggen og synken skriver i — `finnOekt` med teamet — så det som
 * telles her er det som faktisk blir rapporten.
 */
export function AvsluttScreen({ oekt, iDag, paaArbeid }: {
  readonly oekt: Oekt;
  /** Dagen man står på er dagens. Ellers avsluttes ingenting. */
  readonly iDag: boolean;
  /** Tilbake til arbeidsflata når rapporten er levert. */
  paaArbeid(): void;
}): JSX.Element {
  const [anker, setAnker] = useState<nettlogg.Anker | null>(null);
  const [oektId, setOektId] = useState<string | null>(null);
  const [poster, setPoster] = useState<readonly nettlogg.Loggpost[]>([]);
  const [klar, setKlar] = useState(false);
  const [levert, setLevert] = useState<readonly avslutt.Levert[]>([]);

  const [oppsummering, setOppsummering] = useState("");
  const [skift, setSkift] = useState<string>(() => avslutt.skiftNaa());
  const [dato, setDato] = useState<string>(() => avslutt.datoIdag());
  const [armert, setArmert] = useState(false);
  const [leverer, setLeverer] = useState(false);
  const [feil, setFeil] = useState("");
  const [kvittering, setKvittering] = useState("");
  const armTimer = useRef(0);

  const produksjonId = oekt.production.id;
  const opptaksdag = oekt.shootDay;
  const medlemId = oekt.member.id;
  const teamId = oekt.member.unitId ?? null;

  useEffect(() => {
    let avbrutt = false;
    void (async () => {
      const spec = timecodeSpec(oekt.production.framerate, oekt.production.dropFrame);
      const a = await nettlogg.ankerFor(
        produksjonId, opptaksdag, medlemId, spec,
        oekt.production.framerate, oekt.production.dropFrame,
      );
      const id = await nettlogg.finnOekt(produksjonId, opptaksdag, medlemId, teamId);
      const p = id === null ? [] : await nettlogg.hentPoster(id);
      const l = await avslutt.hentLevert(id);
      if (avbrutt) return;
      setAnker(a);
      setOektId(id);
      setPoster(p);
      setLevert(l);
      setKlar(true);
    })();
    return () => { avbrutt = true; };
  }, [produksjonId, opptaksdag, medlemId, teamId,
    oekt.production.framerate, oekt.production.dropFrame]);

  useEffect(() => () => { window.clearTimeout(armTimer.current); }, []);

  const lever = useCallback(async (): Promise<void> => {
    if (anker === null || oektId === null || leverer) return;
    setLeverer(true);
    setFeil("");
    const utfall = await avslutt.lever({
      oekt,
      oektId,
      anker,
      spec: timecodeSpec(oekt.production.framerate, oekt.production.dropFrame),
      poster,
      oppsummering: oppsummering.trim(),
      skift,
      datoIso: dato,
    });
    setLeverer(false);
    if (!utfall.ok) { setFeil(utfall.feil); return; }
    setKvittering(`Rapport v${utfall.versjon} er levert. Den ligger under Rapporter.`);
    setOppsummering("");
    setLevert(await avslutt.hentLevert(oektId));
  }, [anker, oektId, leverer, oekt, poster, oppsummering, skift, dato]);

  /*
   * To trykk, som i appen.
   *
   * «Avslutt dagen» skal være lett å finne og vanskelig å treffe ved uhell.
   * Ingen dialogboks: knappen armerer seg selv og går tilbake etter fire
   * sekunder. Den brukes av en som er sliten, sent i et langt skift.
   */
  const trykk = useCallback((): void => {
    if (leverer) return;
    if (!armert) {
      setArmert(true);
      window.clearTimeout(armTimer.current);
      armTimer.current = window.setTimeout(() => setArmert(false), 4000);
      return;
    }
    window.clearTimeout(armTimer.current);
    setArmert(false);
    void lever();
  }, [armert, leverer, lever]);

  if (!klar) {
    return (
      <div className="flate"><div className="flate__innhold">
        <p className="synk__melding">Henter dagen …</p>
      </div></div>
    );
  }

  return (
    <div className="flate">
      <div className="flate__innhold">
        <section className="avsluttnett" aria-label="Avslutt dagen">
          <span className="avsluttnett__tittel">
            Avslutt dagen · opptaksdag {opptaksdag}
          </span>

          {feil === "" ? null : (
            <p className="synk__melding synk__melding--feil">{feil}</p>
          )}
          {kvittering === "" ? null : (
            <p className="avsluttnett__kvittering">{kvittering}</p>
          )}

          {levert.length === 0 ? null : (
            <p className="avsluttnett__hjelp">
              {levert.length === 1
                ? `Det er alt levert én rapport for dagen (v${levert[0]?.versjon}).`
                : `Det er alt levert ${levert.length} rapporter for dagen.`}
              {" "}En ny versjon legger seg ved siden av den forrige — den
              erstatter den ikke.
            </p>
          )}

          {!iDag ? (
            <p className="synk__melding">
              Du står på opptaksdag {opptaksdag}, som ikke er dagen som går.
              Dagen avsluttes der den logges — trykk datoen i toppen for å
              komme tilbake til i dag.
            </p>
          ) : oektId === null || poster.length === 0 ? (
            <p className="synk__melding">
              Det er ingen logg på denne dagen ennå. Rapporten lages av det som
              står i loggen — skriv i Livelogg eller Synk først.
            </p>
          ) : (
            <>
              <p className="avsluttnett__hjelp">
                {poster.length} {poster.length === 1 ? "post" : "poster"} blir
                rapporten. Synkene er med — de ligger i samme logg. Skriver
                noen mer etterpå, står det under streken og er ikke med.
              </p>

              <textarea
                className="avsluttnett__felt"
                value={oppsummering}
                rows={4}
                placeholder="Oppsummering til redaktøren (kan stå tom)"
                aria-label="Oppsummering"
                onChange={(e) => { setOppsummering(e.target.value); }}
              />

              <div className="avsluttnett__rad">
                <label className="avsluttnett__merke">
                  Skift
                  <select
                    className="avsluttnett__velger"
                    value={skift}
                    onChange={(e) => { setSkift(e.target.value); }}
                  >
                    {avslutt.SKIFT.map((sk) => <option key={sk} value={sk}>{sk}</option>)}
                  </select>
                </label>
                <label className="avsluttnett__merke">
                  Dato
                  <input
                    className="avsluttnett__velger"
                    type="date"
                    value={dato}
                    onChange={(e) => { setDato(e.target.value); }}
                  />
                </label>
                {/* Teamet står alt på medlemsraden, og rapporten arver det.
                    Et felt som ikke lar seg redigere er verre enn ingen — se
                    feltappen, der det var akkurat det. */}
                <span className="avsluttnett__merke">
                  Team
                  <span className="avsluttnett__verdi">
                    {(oekt.member.unitName ?? "") === "" ? "—" : oekt.member.unitName}
                  </span>
                </span>
              </div>

              <div className="avsluttnett__knapper">
                <button
                  type="button"
                  className={`synk__knapp synk__knapp--hoved${
                    armert ? " avsluttnett__armert" : ""}`}
                  onClick={trykk}
                  disabled={leverer || anker === null}
                >
                  {leverer ? "Lager rapporten …"
                    : armert ? "Trykk igjen for å levere"
                    : "Lever rapporten"}
                </button>
                <button
                  type="button"
                  className="synk__knapp"
                  onClick={paaArbeid}
                  disabled={leverer}
                >
                  Tilbake til loggen
                </button>
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
